import { Page } from '../components/page/Page.tsx';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useEffect, useState } from 'react';
import { MissionDetails } from './components/MissionDetails.tsx';
import { useEditableMissionState } from '../missions/model/useEditableMissionState.tsx';
import { HistoryViewModel } from '../app/HistoryViewModel.ts';
import { AuthStatusAlert } from './components/AuthStatusAlert.ts';
import { Blur } from '../components/spinner/Blur.tsx';
import { Refresher } from '../components/refresher/Refresher.tsx';
import { usePageView } from '../support/tracking/usePageView.tsx';
import { Spinner } from '../components/spinner/Spinner.tsx';

export const MissionDetailsPage = ({ missionId, cancelOnMount }: { missionId: string, cancelOnMount?: boolean }) => {
    usePageView('Mission Details');

    const history = useInstance(HistoryViewModel);

    const searchParams = history.getCurrentSearchParamMap();
    const backTo = searchParams.backTo;

    const needsAuthAlert = (history.popSearchParam({ key: 'alert' }) === 'true');

    const [waiting, setWaiting] = useState(needsAuthAlert);
    const { showAlert } = useInstance(AuthStatusAlert);

    const missionVM = useEditableMissionState(missionId);

    useEffect(() => {
        if (missionVM && waiting) {
            if (missionVM.hasLAANC() && !missionVM.isFullyAuthorized() && !missionVM.isNotValid()) {
                // if pending, show spinner with blur for a while, then show alert
                let counter = 0;
                const interval = setInterval(async () => {
                    counter++;
                    try {
                        await missionVM.refresh();
                    } catch (e: any) {
                        console.error('Failed calling mission.refresh()', e);
                        counter = 100; // abort
                    }
                    if (counter >= 6 || missionVM.isFullyAuthorized() || missionVM.isNotValid()) {
                        clearInterval(interval);
                        setWaiting(false);
                        showAlert(missionVM);
                    }
                }, 2000);
            } else {
                // otherwise, show alert immediately
                setWaiting(false);
                showAlert(missionVM);
            }
        };
    }, [missionVM, waiting, showAlert]);

    const onBack = () => {
        if (backTo) {
            // use replace() instead of back() here because when you just created a new mission
            // the previous page is /map
            history.replace(backTo);
        } else {
            history.back('/missions');
        }
    };

    return (
        <Page title="Mission Details" onBack={onBack} onAction={missionVM?.showOtherActions}>
            <>
                {!missionVM && (
                    <Spinner centered />
                )}
                {missionVM && (
                    <>
                        <Refresher onRefresh={missionVM!.refresh} />
                        <Blur active={waiting}>
                            <MissionDetails mission={missionVM} cancelOnMount={cancelOnMount} />
                        </Blur>
                    </>
                )}
            </>
        </Page>
    );
};
